import React from 'react'
import styled from 'styled-components'

import { applyTextStyle } from '../../styles/typography'
import { mqTo } from '../../styles/mediaQueries'

interface IProps {
  children: React.ReactNode
  chips?: React.ReactNode
  search?: React.ReactNode
  actions?: React.ReactNode
  /** Sits just left of the search field, outside it — e.g. a view toggle. */
  leadingActions?: React.ReactNode
  label?: string
  className?: string
}

const FilterBar: React.FC<IProps> = ({
  children,
  chips,
  search,
  actions,
  leadingActions,
  label,
  className,
}) => (
  <Wrapper className={className}>
    <TopRow>
      <FilterColumn>
        {label && <Label>{label}</Label>}
        <Row>{children}</Row>
      </FilterColumn>
      {/* Its own group rather than a child of TrailingGroup, whose
          :not(:last-child) rule is what makes the search field grow. */}
      {leadingActions && <LeadingGroup>{leadingActions}</LeadingGroup>}
      {(search || actions) && (
        <TrailingGroup>
          {search}
          {actions}
        </TrailingGroup>
      )}
    </TopRow>
    {chips}
  </Wrapper>
)

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const FilterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 999 1 auto;
`

const Label = styled.span`
  ${applyTextStyle('labelSmallCaps')}
  color: ${({ theme }) => theme.colors.textDark};
  align-self: flex-start;
  text-align: left;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  // On mobile, keep the filters on a single horizontally scrollable row
  // instead of wrapping onto multiple lines.
  ${mqTo.M`
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    > * {
      flex: 0 0 auto;
    }
  `}
`

const LeadingGroup = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`

const TrailingGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;

  > *:not(:last-child) {
    flex: 1 1 auto;
    min-width: 0;
  }

  // On mobile the search sits on its own full-width line above the filters.
  ${mqTo.M`
    flex: 1 0 100%;
    order: -1;
  `}
`

export default FilterBar
